import React, { useRef, useEffect, useState } from "react";
import ParametersStyle from "./Parameters.module.scss";
import Input from "../Input/Input.tsx";
import Button from "../Button/Button.tsx";
import CameraLock from "../Icons/CameraLock.tsx";
import CameraUnlock from "../Icons/CameraUnlock.tsx";
import Menu from "../Icons/Menu.tsx";
import Cross from "../Icons/Cross.tsx";
import { useCustomScrollbar } from "../CustomScrollbar/useCustomScrollbar.ts";

interface Props {
  distance: number;
  lane: number;
  time: number;
  timePerKm?: string;
  isPlay: boolean;
  setPlay: React.Dispatch<React.SetStateAction<boolean>>;
  setDistance: React.Dispatch<React.SetStateAction<number>>;
  setLane: React.Dispatch<React.SetStateAction<number>>;
  setTime: React.Dispatch<React.SetStateAction<number>>;
  setTimePerKm: React.Dispatch<React.SetStateAction<string | undefined>>;
  onChange: (name: string, value: number | string) => void;
  handleClick: () => void;
  handleClickCamera: () => void;
  cameraLocked: boolean;
}

const formatTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const cs = Math.floor((seconds - Math.floor(seconds)) * 100);
  return `${h.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}:${s.toString().padStart(2, "0")}:${cs
      .toString()
      .padStart(2, "0")}`;
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

const Parameters = ({
  distance,
  lane,
  time,
  timePerKm,
  handleClick,
  handleClickCamera,
  setLane,
  isPlay,
  setPlay,
  onChange,
  setTimePerKm,
  cameraLocked,
}: Props): React.JSX.Element => {
  const [isOpen, setOpen] = useState<boolean>(true);
  const width = useWindowWidth();
  const checkpointsHostRef = useRef<HTMLDivElement>(null);
  const checkpointsViewportRef = useRef<HTMLDivElement>(null);
  useCustomScrollbar(checkpointsHostRef, checkpointsViewportRef);
  const checkpoints: { distance: number; time: string }[] = [];
  const interval = 100;

  for (let d = interval; d <= distance; d += interval) {
    const t = (d / distance) * time;
    checkpoints.push({ distance: d, time: formatTime(t) });
  }

  const prevRef = useRef<string>(timePerKm ?? "");
  useEffect(() => {
    prevRef.current = timePerKm ?? "";
  }, [timePerKm]);

  const handleTimePerKmChange = (value: string) => {
    let v = value.replace(/[^\d:]/g, "");

    if (v.includes(":")) {
      const parts = v.split(":");
      const left = (parts[0] ?? "").replace(/\D/g, "").slice(0, 2);
      const right = (parts[1] ?? "").replace(/\D/g, "").slice(0, 2);
      v = left + (v.includes(":") && (right.length > 0 || value.endsWith(":")) ? ":" + right : "");
      if (v.length > 5) v = v.slice(0, 5);
      prevRef.current = v;
      setTimePerKm(v);
      return;
    }

    const digits = v.replace(/\D/g, "").slice(0, 4);

    if (digits.length <= 2) {
      v = digits;
    } else {
      v = digits.slice(0, 2) + ":" + digits.slice(2);
    }

    prevRef.current = v;
    setTimePerKm(v);
  };

  const handleTimePerKmBlur = () => {
    const cur = timePerKm ?? "";
    if (cur === "") {
      setTimePerKm(undefined);
      return;
    }
    const [mmRaw, ssRaw] = cur.split(":");
    const mm = (mmRaw ?? "").replace(/\D/g, "").padStart(2, "0").slice(-2);
    const ss = (ssRaw ?? "").replace(/\D/g, "").padStart(2, "0").slice(-2);
    setTimePerKm(`${mm}:${ss}`);
  };

  return (
    <div className={`${ParametersStyle.parametersContainer} ${isOpen ? ParametersStyle.close : ParametersStyle.open}`}>
      <h1>
        Track Visualizer
        {width < 750 ? (
          <Button specialClass={`${ParametersStyle.menuButton}`} isBackgroundTransparent={true} icon={isOpen ? <Menu /> : <Cross />} handleClick={() => setOpen(!isOpen)} padding={[0, 0]} />
        ) : (
          <span>*</span>
        )}
      </h1>
      <div className={ParametersStyle.wrapperInputs}>
        <Input
          name="distance"
          placeholder="Write a distance"
          specialClass="distanceInput"
          type="number"
          value={distance}
          text="Distance (m)"
          onChange={onChange}
          min={1}
          max={10000}
        />
        <Input
          name="lane"
          placeholder="Write a lane 1 - 8"
          specialClass="laneInput"
          type="text"
          value={lane.toString()}
          text="Lane (1-8)"
          onChange={(name, value) => {
            const digits = (value as string).replace(/\D/g, "");

            if (digits === "") {
              onChange(name, "");
              return;
            }
            const num = parseInt(digits, 10);
            if (!isNaN(num)) {
              onChange(name, Math.min(8, Math.max(1, num)));
            }
          }}
        />
      </div>
      <Input
        name="time"
        placeholder="Write a time in s"
        specialClass="timeInput"
        type="number"
        value={time}
        text="Time (s/distance)"
        min={1}
        max={7200}
        onChange={onChange}
      />
      <Input
        name="timePerKm"
        placeholder="mm:ss"
        specialClass="timeInput"
        type="text"
        value={timePerKm ?? ""}
        text="Temps par km (mm:ss)"
        onChange={(name, value) => handleTimePerKmChange(value as string)}
        onBlur={handleTimePerKmBlur}
      />
      <div className={ParametersStyle.wrapperBtns}>
        <Button
          specialClass="buttonVisualizer"
          text="Lancer la simulation"
          handleClick={handleClick}
          padding={[13, 15]}
        />
        <div className={ParametersStyle.wrapperBtns}>
          <Button
            specialClass="lock"
            handleClick={handleClickCamera}
            icon={<CameraLock />}
            isUnlock={!cameraLocked}
          />
          <Button
            specialClass="unlock"
            handleClick={handleClickCamera}
            icon={<CameraUnlock />}
            isUnlock={cameraLocked}
          />
        </div>
      </div>
      <div ref={checkpointsHostRef} className={ParametersStyle.checkpoints} data-overlayscrollbars-initialize="">
        <div ref={checkpointsViewportRef} className={ParametersStyle.checkpointsViewport} data-overlayscrollbars-viewport="scrollbarHidden">
          <div className={ParametersStyle.checkpointsContent}>
            <span>Temps de passages</span>
            {checkpoints.map((cp) => (
              <div
                key={cp.distance}
                className={ParametersStyle.checkpoint}
                data-distance={cp.distance}
                data-time={cp.time}
              >
                <span>
                  {cp.distance}m - {cp.time}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Parameters;
